import { useTranslation } from "react-i18next";

import { buildChangeTypeFilters } from "./buildChangeTypeFilters";
import { SegmentHistoryFilters } from "./SegmentHistoryFilters";
import type { ChangeTypeCount } from "../api/types";

import { DateRangePicker } from "@/components/ui/v2/date-range-picker";
import type { DateRange } from "@/components/ui/v2/date-range-picker";
import { TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";

interface SegmentHistoryToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  changeTypes: string[];
  onChangeTypesChange: (changeTypes: string[]) => void;
  changeTypeCounts: ChangeTypeCount[];
  totalCount?: number;
}

export function SegmentHistoryToolbar({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  changeTypes,
  onChangeTypesChange,
  changeTypeCounts,
  totalCount,
}: SegmentHistoryToolbarProps) {
  const { t } = useTranslation();

  const activeFilters = buildChangeTypeFilters(changeTypes, t);

  const handleRemoveFilter = (filter: { key: string; value: string | number }) => {
    onChangeTypesChange(changeTypes.filter((ct) => ct !== filter.value));
  };

  return (
    <div className="space-y-4">
      <TableToolbar
        searchValue={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={t("admin.segments.history.searchPlaceholder")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {totalCount !== undefined && (
              <span className="text-sm text-muted-foreground">
                {t("admin.segments.history.eventCount", { count: totalCount })}
              </span>
            )}
            <SegmentHistoryFilters
              selected={changeTypes}
              onChange={onChangeTypesChange}
              changeTypeCounts={changeTypeCounts}
            />
            <DateRangePicker
              initialDateFrom={dateRange.from}
              initialDateTo={dateRange.to}
              maxDate={new Date()}
              onUpdate={({ range }) => onDateRangeChange(range)}
              align="end"
            />
          </div>
        }
      />
      {activeFilters.length > 0 && (
        <TableActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={() => onChangeTypesChange([])}
        />
      )}
    </div>
  );
}
